/**
 * @file state.js
 * @description 全局状态管理 - 观察者模式
 * 
 * @features
 * - 集中式状态管理
 * - 观察者模式（Subscribe/Notify）
 * - 用户认证状态
 * - 数据缓存管理
 * - UI 状态管理
 * - 通知系统
 * 
 * @state
 * - user: 当前用户信息
 * - isAuthenticated: 登录状态
 * - currentPage: 当前页面
 * - venues: 场馆数据缓存
 * - socialFeed: 社交动态缓存
 * - notification: 通知消息
 * 
 * @methods
 * - getState(): 获取当前状态
 * - setState(updates): 更新状态
 * - subscribe(callback): 订阅状态变化
 * - showNotification(message, type): 显示通知
 * 
 * @author AAA Team
 * @created 2025-09-01
 * @updated 2025-10-06
 */

class AppState {
  constructor() {
    this.state = {
      // 用户相关
      user: null,
      isAuthenticated: false,
      
      // 页面相关
      currentPage: 'home',
      loading: false,
      error: null,
      
      // 数据缓存
      venues: [],
      venuesCache: new Map(),
      bookings: [],
      socialFeed: [],
      friends: [],
      
      // UI状态
      showModal: false,
      modalContent: null,
      notification: null
    }
    
    this.listeners = []
    this.loadFromStorage()
  }

  /**
   * 获取当前状态
   */
  getState() {
    return { ...this.state }
  }

  /**
   * 更新状态
   */
  setState(updates) {
    this.state = { ...this.state, ...updates }
    this.notify()
    this.saveToStorage()
  }

  /**
   * 订阅状态变化
   */
  subscribe(listener) {
    this.listeners.push(listener)
    // 返回取消订阅函数
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener)
    }
  }

  /**
   * 通知所有订阅者
   */
  notify() {
    this.listeners.forEach(listener => {
      try {
        listener(this.state)
      } catch (error) {
        console.error('Listener error:', error)
      }
    })
  }

  /**
   * 设置加载状态
   */
  setLoading(loading) {
    this.setState({ loading })
  }

  /**
   * 设置错误
   */
  setError(error) {
    this.setState({ error })
    
    // 3秒后自动清除错误
    if (error) {
      setTimeout(() => {
        if (this.state.error === error) {
          this.setState({ error: null })
        }
      }, 3000)
    }
  }

  /**
   * 显示通知
   */
  showNotification(message, type = 'info') {
    this.setState({
      notification: { message, type, timestamp: Date.now() }
    })
    
    // 3秒后自动清除
    setTimeout(() => {
      this.setState({ notification: null })
    }, 3000)
  }

  /**
   * 设置用户信息
   */
  setUser(user) {
    this.setState({
      user,
      isAuthenticated: !!user
    })
  }

  /**
   * 清除用户信息
   */
  clearUser() {
    this.setState({
      user: null,
      isAuthenticated: false,
      bookings: [],
      socialFeed: []
    })
  }

  /**
   * 缓存场馆数据
   */
  cacheVenue(venue) {
    const cache = new Map(this.state.venuesCache)
    cache.set(venue.id, venue)
    this.setState({ venuesCache: cache })
  }

  /**
   * 获取缓存的场馆
   */
  getCachedVenue(id) {
    return this.state.venuesCache.get(id)
  }

  /**
   * 保存到localStorage
   */
  saveToStorage() {
    try {
      const dataToSave = {
        user: this.state.user,
        isAuthenticated: this.state.isAuthenticated
      }
      localStorage.setItem('aaa_app_state', JSON.stringify(dataToSave))
    } catch (error) {
      console.error('Save to storage failed:', error)
    }
  }

  /**
   * 从localStorage加载
   */
  loadFromStorage() {
    try {
      const saved = localStorage.getItem('aaa_app_state')
      if (saved) {
        const data = JSON.parse(saved)
        this.state = { ...this.state, ...data }
      }
    } catch (error) {
      console.error('Load from storage failed:', error)
    }
  }

  /**
   * 清空缓存
   */
  clearCache() {
    this.setState({
      venues: [],
      venuesCache: new Map(),
      bookings: [],
      socialFeed: [],
      friends: []
    })
  }
}

// 导出单例
export const appState = new AppState()

// 监听认证退出事件
window.addEventListener('auth:logout', () => {
  appState.clearUser()
  appState.clearCache()
})
